// SPA → Pipedrive proxy client.
//
// The Cloudflare Worker at worker/src/pipedrive-sync.ts exposes a narrow
// PATCH/POST surface the SPA can call to push writes into Pipedrive. The
// worker holds the API token and verifies a Supabase Auth Bearer JWT on
// every request; nothing sensitive lives in the browser.
//
// Usage pattern in components:
//   1. Update local state optimistically (fast, 0 network)
//   2. Call proxyPatchDeal / proxyWonDeal / proxyLostDeal
//   3. On error, roll back local state
//   4. Webhook echo from Pipedrive → Supabase realtime reconciles any drift
//
// Env:
//   VITE_PIPEDRIVE_WORKER_URL — base URL of the deployed worker, e.g.
//   https://task-board-pipedrive-sync.<cf-subdomain>.workers.dev

import { supabase } from './supabase';

const WORKER_URL: string | undefined = (import.meta.env.VITE_PIPEDRIVE_WORKER_URL as string) || undefined;

interface ProxyError {
  ok: false;
  stage?: 'pipedrive' | 'mirror';
  error: string;
}

interface ProxySuccess {
  ok: true;
  id: string | number;
  fields?: string[];
  warning?: string;
  mirrorError?: string;
}

type ProxyResponse = ProxySuccess | ProxyError;

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new Error('not authenticated — log in with Supabase Auth first');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function requireWorkerUrl(): string {
  if (!WORKER_URL) {
    throw new Error(
      'VITE_PIPEDRIVE_WORKER_URL not set. Add it to .env.local and restart the dev server.',
    );
  }
  return WORKER_URL;
}

export interface DealPatch {
  stage_id?: number;
  title?: string;
  value?: number;
  currency?: string;
  expected_close_date?: string | null;
}

export async function proxyPatchDeal(dealId: number, patch: DealPatch): Promise<ProxyResponse> {
  const base = requireWorkerUrl();
  const resp = await fetch(`${base}/proxy/deals/${dealId}`, {
    method: 'PATCH',
    headers: await authHeader(),
    body: JSON.stringify(patch),
  });
  return (await resp.json()) as ProxyResponse;
}

export async function proxyWonDeal(dealId: number): Promise<ProxyResponse> {
  const base = requireWorkerUrl();
  const resp = await fetch(`${base}/proxy/deals/${dealId}/won`, {
    method: 'POST',
    headers: await authHeader(),
  });
  return (await resp.json()) as ProxyResponse;
}

export async function proxyLostDeal(dealId: number): Promise<ProxyResponse> {
  const base = requireWorkerUrl();
  const resp = await fetch(`${base}/proxy/deals/${dealId}/lost`, {
    method: 'POST',
    headers: await authHeader(),
  });
  return (await resp.json()) as ProxyResponse;
}

export function isProxyConfigured(): boolean {
  return Boolean(WORKER_URL);
}
